import { useEffect, useMemo, useState, useCallback } from "react";
import { BiLayer, BiMoney, BiToggleLeft, BiEnvelope } from "react-icons/bi";
import { FaUserGraduate, FaUsers, FaUserTie } from "react-icons/fa";
import Pagination from "../../../components/lib/pagination";
import SpeedDial from "../../../components/lib/speedDial";
import CentralModal from "../../../components/lib/centralModal";
import FilterBar from "../../../components/reusables/filterBar/filterBar";
import BulkActionBar, { BulkActionButton } from "../../../components/reusables/bulkActions/bulkActionBar";
import useFilters from "../../../components/reusables/filterBar/hooks/useFilters";
import { useAuth } from "../../../auth/AuthenticationWrapper";
import { useCustomerData } from "../hooks/useCustomerData";
import { useCustomerSelection } from "../hooks/useCustomerSelection";
import StudentRegisterModal from "../components/students/studentRegisterModal";
import ParentRegisterModal from "../components/parents/parentRegisterModal";
import EmployeeRegisterModal from "../components/employees/employeeRegisterModal";
import StudentDetailsModalContent from "../components/students/studentDetailsModal";
import ParentDetailsModalContent from "../components/parents/parentDetailsModal";
import EmployeeDetailsModalContent from "../components/employees/employeeDetailsModal";
import BatchGradeChangeModal from "../components/bulkActions/bacthGradeChangeModal";
import ConfirmActionModal from "../components/bulkActions/confirmActionModal";
import BatchStatusChangeModal from "../components/bulkActions/batchStatusChangeModal";
import ClientService from "../../../rest/clientService";
import CustomerList from "./customersList";
import ViewToggle from "../../../components/lib/viewToggle";
import CustomerGrid from "./customerGrid";
import useViewMode from "../../../hooks/useViewMode";
import usePersistedState from "../../../hooks/usePersistedState";

function CustomerTable() {

    const [pageSize, setPageSize] = useState(10);
    const [clientType, setClientType] = useState('students');
    const [gradeOptions, setGradeOptions] = useState([]);
    const [sendCredentialsUponRegister, setSendCredentialsUponRegister] = useState(false);
    const [viewMode, setViewMode] = useViewMode('foodz_customer_view_mode', 'list');

    const [sort, setSort] = usePersistedState(`customers:${clientType}:sort`, {
        field: null,
        direction: null
    });

    const { hasPermission, user } = useAuth();
    const canCreate = hasPermission("customers", "create");

    const { search, setSearch, filters, updateFilter, clearFilters, page, setPage } = useFilters({}, `customers:${clientType}:filters`);

    useEffect(() => {
        const loadPrefs = async () => {
            try {
                const prefs = await ClientService.getClientAdminPreferences();

                if (prefs?.grades) {
                    const gradesArray = prefs.grades
                        .split(";")
                        .map(g => g.trim())
                        .filter(Boolean)
                        .map(g => ({ value: g, label: g }));
                    setGradeOptions(gradesArray);
                }

                if (prefs?.enable_send_credentials) {
                    setSendCredentialsUponRegister(true);
                }

            } catch (err) {
                console.error("Erro prefs:", err);
            }
        };

        loadPrefs();
    }, []);

    const handleSortChange = useCallback((field) => {
        setPage(0);

        setSort(prev => {
            if (prev.field !== field) {
                return { field, direction: 'asc' };
            }

            if (prev.direction === 'asc') {
                return { field, direction: 'desc' };
            }

            return { field: null, direction: null };
        });

    }, [setPage, setSort]);

    // padrão: ativos primeiro, depois alfabético (responsáveis não possuem coluna/campo de status)
    const sortParam = useMemo(() => {
        if (!sort.field || !sort.direction) {
            return clientType === 'parents' ? ['name,asc'] : ['user_status,desc', 'name,asc'];
        }
        const primary = `${sort.field},${sort.direction}`;
        return sort.field === 'name' ? [primary] : [primary, 'name,asc'];
    }, [sort, clientType]);

    const dataFilters = useMemo(() => ({ ...filters, search }), [filters, search]);

    const { pagedData, totalPages, totalElements, loading, reloadData } =
        useCustomerData(clientType, dataFilters, page, pageSize, sortParam, user);

    const selection = useCustomerSelection(pagedData, reloadData);

    const [modals, setModals] = useState({
        registerStudent: false,
        registerParent: false,
        registerEmployee: false,
        batchGrade: false,
        batchStatus: false,
        batchBilling: false,
        batchCredentials: false
    });

    const toggleModal = useCallback((key, value) => {
        setModals(prev => ({ ...prev, [key]: value }));
    }, []);

    const [detailsItem, setDetailsItem] = useState(null);

    const handleLoadChanging = (type) => {
        if (clientType !== type) {
            setClientType(type);
            selection.clearSelection();
        }
    };

    const filterDefinitions = useMemo(() => {

        const commonStatus = {
            key: "status",
            label: "Status",
            placeholder: "Status",
            options: [
                { value: true, label: "Ativo" },
                { value: false, label: "Inativo" }
            ],
            selectedValues: filters.status || [],
            onChange: v => updateFilter("status", v)
        };

        const creditFilter = {
            key: "creditStatus",
            label: "Saldo",
            placeholder: "Saldo",
            options: [
                { value: "positive", label: "Positivo" },
                { value: "negative", label: "Negativo" },
                { value: "zero", label: "Zerado" }
            ],
            selectedValues: filters.creditStatus || [],
            onChange: v => updateFilter("creditStatus", v.slice(-1))
        };

        const gradeFilter = {
            key: "grade",
            label: "Série",
            placeholder: "Turma",
            options: gradeOptions,
            selectedValues: filters.grade || [],
            onChange: v => updateFilter("grade", v)
        };

        if (clientType === 'students')
            return gradeOptions.length > 0
                ? [commonStatus, gradeFilter, creditFilter]
                : [commonStatus, creditFilter];

        if (clientType === 'employees')
            return [commonStatus, creditFilter];

        return [];

    }, [clientType, filters, updateFilter, gradeOptions]);

    const getVisibleSelectedObjects = () => {
        if (!pagedData) return [];
        return pagedData.filter(item =>
            selection.selectedItems.includes(item.login)
        );
    };

    const getModalMessage = () => {
        if (clientType === 'students')
            return `Deseja redefinir os dados para ${selection.selectedItems.length} aluno(s)?`;

        return `Deseja redefinir dados de ${selection.selectedItems.length} usuários?`;
    };

    const handleBatchGrade = async (val) => {
        if (selection.actions?.updateBatchGrades) {
            await selection.actions.updateBatchGrades(val);
            toggleModal('batchGrade', false);
        }
    };

    const handleBatchStatus = async (val) => {
        if (selection.actions?.updateBatchStatus) {
            await selection.actions.updateBatchStatus(val);
            toggleModal('batchStatus', false);
        }
    };

    const handleBatchBilling = async () => {
        if (selection.actions?.sendBatchBilling) {
            await selection.actions.sendBatchBilling();
            toggleModal('batchBilling', false);
        }
    };

    const handleBatchCredentials = async () => {
        if (selection.actions?.sendBatchCredentials) {
            await selection.actions.sendBatchCredentials();
            toggleModal('batchCredentials', false);
        }
    };

    return (
        <div>

            {/* TOP BAR */}
            <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4 mb-6">
                <div className="flex-1 w-full z-10">

                    {selection.selectedItems.length > 0 ? (
                        <BulkActionBar
                            selectedCount={selection.selectedItems.length}
                            onClearSelection={selection.clearSelection}
                        >
                            {clientType === 'students' && (
                                <BulkActionButton
                                    icon={BiLayer}
                                    label="Mudar Série"
                                    onClick={() => toggleModal('batchGrade', true)}
                                />
                            )}

                            {(clientType === 'students' || clientType === 'employees') && (
                                <BulkActionButton
                                    icon={BiToggleLeft}
                                    label="Ativar/Desativar"
                                    variant="primary"
                                    onClick={() => toggleModal('batchStatus', true)}
                                />
                            )}

                            {clientType === 'students' && (
                                <BulkActionButton
                                    icon={BiMoney}
                                    label="Emitir Cobrança"
                                    variant="danger"
                                    onClick={() => toggleModal('batchBilling', true)}
                                />
                            )}

                            <BulkActionButton
                                icon={BiEnvelope}
                                label="Redefinir Senha"
                                onClick={() => toggleModal('batchCredentials', true)}
                            />
                        </BulkActionBar>

                    ) : (
                        <FilterBar
                            searchValue={search}
                            onSearchChange={(val) => { setSearch(val); setPage(0); }}
                            onResetPage={() => setPage(0)}
                            placeholder={`Pesquisar ${clientType === 'students'
                                    ? 'alunos'
                                    : clientType === 'employees'
                                        ? 'colaboradores'
                                        : 'responsáveis'
                                }`}
                            filters={filterDefinitions}
                            onClearFilters={clearFilters}
                            loading={loading}
                        />
                    )}
                </div>

                <div className="flex items-center gap-3">

                    <select
                        className='h-8 pl-3 pr-8 border-2 border-slate-200 rounded-lg bg-white text-sm text-slate-600'
                        value={pageSize}
                        onChange={(e) => {
                            setPageSize(Number(e.target.value));
                            setPage(0);
                        }}
                    >
                        {[10, 20, 30, 50].map(s =>
                            <option key={s} value={s}>{s} itens/pág</option>
                        )}
                    </select>

                    <ViewToggle view={viewMode} onChange={setViewMode} />

                    <SpeedDial
                        disabled={!canCreate}
                        label="Adicionar"
                        buttonType="confirm"
                        actions={[
                            { label: "Aluno", icon: FaUserGraduate, onClick: () => toggleModal('registerStudent', true) },
                            { label: "Responsável", icon: FaUserTie, onClick: () => toggleModal('registerParent', true) },
                            { label: "Colaborador", icon: FaUsers, onClick: () => toggleModal('registerEmployee', true) }
                        ]}
                    />
                </div>
            </div>

            {/* TABS */}
            <table className="table-fixed w-full">
                <thead>
                    <tr className="text-white h-10 font-montserrat text-md">
                        {['students', 'parents', 'employees'].map(type => (
                            <th
                                key={type}
                                className={`rounded-t-lg border-2 border-white ${clientType === type
                                        ? "bg-custom_blue"
                                        : "bg-custom_blue/50"
                                    } cursor-pointer`}
                                onClick={() => handleLoadChanging(type)}
                            >
                                {type === 'students'
                                    ? 'Alunos'
                                    : type === 'parents'
                                        ? 'Responsáveis'
                                        : 'Colaboradores'}
                            </th>
                        ))}
                    </tr>
                </thead>
            </table>

            {/* DATA VIEW */}
            {loading ? (
                <div className="w-full py-10 text-center bg-white border-x-2 border-b-2 text-gray-500 animate-pulse font-medium">
                    Carregando dados...
                </div>
            ) : viewMode === 'list' ? (
                <CustomerList
                    data={pagedData}
                    clientType={clientType}
                    selectedItems={selection.selectedItems}
                    onSelectAll={selection.handleSelectAll}
                    onSelectItem={selection.handleSelectItem}
                    onSortChange={handleSortChange}
                    sortField={sort}
                    onOpenDetails={setDetailsItem}
                />
            ) : (
                <CustomerGrid
                    data={pagedData}
                    clientType={clientType}
                    onOpenDetails={setDetailsItem}
                />
            )}

            {/* PAGINATION */}
            <div className="py-4">
                {totalPages > 1 && (
                    <Pagination
                        currentPage={page}
                        totalPages={totalPages}
                        onPageChange={setPage}
                        totalItems={totalElements}
                        pageSize={pageSize}
                    />
                )}
            </div>

            <StudentRegisterModal isOpen={modals.registerStudent} setIsOpen={(v) => toggleModal('registerStudent', v)} sendCredentials={sendCredentialsUponRegister} />
            <ParentRegisterModal isOpen={modals.registerParent} setIsOpen={(v) => toggleModal('registerParent', v)} sendCredentials={sendCredentialsUponRegister} />
            <EmployeeRegisterModal isOpen={modals.registerEmployee} setIsOpen={(v) => toggleModal('registerEmployee', v)} />

            <CentralModal
                isOpen={!!detailsItem}
                onClose={() => setDetailsItem(null)}
                title={clientType === 'students' ? "Detalhes do Aluno" : clientType === 'employees' ? "Detalhes do Colaborador" : "Detalhes do Responsável"}
                className="md:max-w-lg lg:max-w-3xl xl:max-w-5xl md:px-4 md:-mr-16"
            >
                {detailsItem && clientType === 'students' && (
                    <StudentDetailsModalContent client={detailsItem} onEdit={() => setDetailsItem(null)} onDelete={() => setDetailsItem(null)} />
                )}
                {detailsItem && clientType === 'parents' && (
                    <ParentDetailsModalContent client={detailsItem} onEdit={() => setDetailsItem(null)} onDelete={() => setDetailsItem(null)} />
                )}
                {detailsItem && clientType === 'employees' && (
                    <EmployeeDetailsModalContent client={detailsItem} onEdit={() => setDetailsItem(null)} onDelete={() => setDetailsItem(null)} />
                )}
            </CentralModal>

            <BatchGradeChangeModal
                isOpen={modals.batchGrade}
                onClose={() => toggleModal('batchGrade', false)}
                selectedStudents={getVisibleSelectedObjects()}
                gradeOptions={gradeOptions}
                onConfirm={handleBatchGrade}
            />
            <BatchStatusChangeModal
                isOpen={modals.batchStatus}
                onClose={() => toggleModal('batchStatus', false)}
                selectedUsers={getVisibleSelectedObjects()}
                onConfirm={handleBatchStatus}
            />
            <ConfirmActionModal
                isOpen={modals.batchBilling}
                onClose={() => toggleModal('batchBilling', false)}
                selectedStudents={getVisibleSelectedObjects()}
                title="Emitir Cobrança em Massa"
                message={`Deseja enviar cobrança para ${selection.selectedItems.length} clientes?`}
                confirmText="Enviar"
                onConfirm={handleBatchBilling}
            />
            <ConfirmActionModal
                isOpen={modals.batchCredentials}
                onClose={() => toggleModal('batchCredentials', false)}
                selectedItems={getVisibleSelectedObjects()}
                title="Enviar Credenciais"
                message={getModalMessage()}
                confirmText="Enviar"
                onConfirm={handleBatchCredentials}
            />
        </div>
    );
}

export default CustomerTable;